/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  VerifyOnboardingHooksSetupRequest,
  VerifyOnboardingHooksSetupSecurity,
} from "../models/operations/verifyonboardinghookssetup.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildVerifyOnboardingHooksSetupQuery,
  prefetchVerifyOnboardingHooksSetup,
  queryKeyVerifyOnboardingHooksSetup,
  VerifyOnboardingHooksSetupQueryData,
} from "./verifyOnboardingHooksSetup.core.js";
export {
  buildVerifyOnboardingHooksSetupQuery,
  prefetchVerifyOnboardingHooksSetup,
  queryKeyVerifyOnboardingHooksSetup,
  type VerifyOnboardingHooksSetupQueryData,
};

export type VerifyOnboardingHooksSetupQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * verifyOnboardingHooksSetup organizations
 *
 * @remarks
 * Return recent hook events for the active organization so the onboarding wizard can confirm that coding agent instrumentation is delivering events to Speakeasy. Polled from the confirm-traffic step.
 */
export function useVerifyOnboardingHooksSetup(
  request?: VerifyOnboardingHooksSetupRequest | undefined,
  security?: VerifyOnboardingHooksSetupSecurity | undefined,
  options?: QueryHookOptions<
    VerifyOnboardingHooksSetupQueryData,
    VerifyOnboardingHooksSetupQueryError
  >,
): UseQueryResult<
  VerifyOnboardingHooksSetupQueryData,
  VerifyOnboardingHooksSetupQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildVerifyOnboardingHooksSetupQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * verifyOnboardingHooksSetup organizations
 *
 * @remarks
 * Return recent hook events for the active organization so the onboarding wizard can confirm that coding agent instrumentation is delivering events to Speakeasy. Polled from the confirm-traffic step.
 */
export function useVerifyOnboardingHooksSetupSuspense(
  request?: VerifyOnboardingHooksSetupRequest | undefined,
  security?: VerifyOnboardingHooksSetupSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    VerifyOnboardingHooksSetupQueryData,
    VerifyOnboardingHooksSetupQueryError
  >,
): UseSuspenseQueryResult<
  VerifyOnboardingHooksSetupQueryData,
  VerifyOnboardingHooksSetupQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildVerifyOnboardingHooksSetupQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setVerifyOnboardingHooksSetupData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      sinceUnixNano?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: VerifyOnboardingHooksSetupQueryData,
): VerifyOnboardingHooksSetupQueryData | undefined {
  const key = queryKeyVerifyOnboardingHooksSetup(...queryKeyBase);

  return client.setQueryData<VerifyOnboardingHooksSetupQueryData>(key, data);
}

export function invalidateVerifyOnboardingHooksSetup(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      sinceUnixNano?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizations",
      "verifyOnboardingHooksSetup",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllVerifyOnboardingHooksSetup(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "organizations", "verifyOnboardingHooksSetup"],
  });
}
